import {
    VerticalFlex,
    PadContainer,
    AnimatedPageLoader,
    Ticket,
    TicketHeader,
    AccentLabel,
    Label,
    TicketBody,
} from 'smartsuite-ui';
import { DateTimeFormatter } from 'brock-date-time';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation, Trans } from 'react-i18next';
import { getBagEventsUri } from '../constants/apiConstants';

const EVENT_ENUMS = [
    'LuggageCheckedAtEvent',
    'LuggageReceivedByBagCheck',
    'LuggageInTransit',
    'LuggageAtResort',
];

function BagEventsPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { reservationId, reservationLastName } = useParams();

    const bagTrackingQuery = useQuery({
        queryKey: ['bagTracking', reservationId, reservationLastName],
        queryFn: async () => {
            const response = await fetch(getBagEventsUri(reservationId));

            // TODO: Handle 404 specifically on same page with message
            if (response.status === 404) {
                return null;
            }

            if (!response.ok) {
                navigate('/');
                return;
            }

            const parsedResponse = await response.json();
            return parsedResponse;
        },
    });
    const { data: bagEvent } = bagTrackingQuery;

    if (bagEvent === undefined) {
        return <AnimatedPageLoader />;
    }

    if (bagEvent === null) {
        return (
            <div className="page-wrapper">
                <div className="bag-tracking-card">
                    <header className="bag-tracking-card__header">
                        <div className="bag-tracking-card__title">
                            {t('bagTrackingPageTitle')}
                        </div>
                    </header>
                    <p>
                        <Trans
                            i18nKey={'bagTrackingPageNoEventsDescription'}
                            components={[
                                // eslint-disable-next-line jsx-a11y/anchor-has-content
                                <a href="mailto:bcsupport@bagcheck.com" />,
                            ]}
                        />
                    </p>
                    <button
                        className="btn btn--primary"
                        onClick={() => navigate('..')}
                    >
                        {t('backToEnrollment')}
                    </button>
                </div>
            </div>
        );
    }

    function renderEvent(event, index, length) {
        const isPrevious = index !== length - 1;

        let bagEventClass = 'bag-event__body';
        if (isPrevious) {
            bagEventClass += ' bag-event__body--previous';
        }
        if (index === length - 1) {
            bagEventClass += ' bag-event__body--last';
        }

        const bagCheckIcon = 'fas fa-check-circle';
        let iconClass = isPrevious
            ? `bag-event__icon bag-event__icon--previous ${bagCheckIcon}`
            : `bag-event__icon fas ${bagCheckIcon}`;

        return (
            <div
                className="bag-event__container"
                key={`bag-event__container-${index}`}
            >
                <div className={bagEventClass}>
                    <span className={iconClass} />
                    <span className="event-description">
                        {t(event.eventTypeEnum, event)}
                    </span>
                    <div className="bag-event__info-container">
                        <span className="event-timestamp">
                            {DateTimeFormatter.withFormatString(
                                event.eventTimestamp,
                                'MM/DD/YYYY h:mm A',
                            )}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    function latestBagEventValue(bagEvents) {
        return bagEvents
            .filter((event) => EVENT_ENUMS.includes(event.eventTypeEnum))
            .at(-1).eventTypeEnum;
    }

    return (
        <div className="page-wrapper">
            <div className="bag-tracking-card">
                <header className="bag-tracking-card__header">
                    <div className="bag-tracking-card__title">
                        {t('bagTrackingPageTitle')}
                    </div>
                </header>
                <div className="bag-tracking-card__subtitle">
                    <span>{t('bagTrackingPageSubtitle')}</span>
                </div>
                <p>{t('bagTrackingPageDescription')}</p>
                {bagEvent.passengers.map((passenger, index) => (
                    <div key={passenger.paxId}>
                        {index > 0 && (
                            <PadContainer cssClassModifiers={['pad-small']} />
                        )}
                        <div className="bag-tracking-card__subtitle">{`${passenger.lastName}, ${passenger.firstName}`}</div>
                        {passenger.bags.map((bag, index) => (
                            <div key={bag.bagTag}>
                                {index > 0 && (
                                    <PadContainer
                                        cssClassModifiers={['pad-small']}
                                    />
                                )}
                                <Ticket>
                                    <TicketHeader cssClassModifiers={['small']}>
                                        <VerticalFlex>
                                            <AccentLabel>
                                                {t('bagTagNumber')}
                                            </AccentLabel>
                                            <Label>
                                                <span className="bag-tracking-card__bag-tag-number">
                                                    {bag.bagTag}
                                                </span>
                                            </Label>
                                        </VerticalFlex>
                                    </TicketHeader>
                                    <TicketBody>
                                        <PadContainer
                                            cssClassModifiers={['pad-small']}
                                        />
                                        {bag.bagEvents
                                            .filter((event) =>
                                                EVENT_ENUMS.includes(
                                                    event.eventTypeEnum,
                                                ),
                                            )
                                            .map((event, index, events) =>
                                                renderEvent(
                                                    event,
                                                    index,
                                                    events.length,
                                                ),
                                            )}
                                    </TicketBody>
                                </Ticket>
                                <p>
                                    {
                                        <Trans
                                            i18nKey={`${latestBagEventValue(
                                                bag.bagEvents,
                                            )}HelperText`}
                                            components={[
                                                latestBagEventValue(
                                                    bag.bagEvents,
                                                ) === 'LuggageAtResort' ? (
                                                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                                                    <a
                                                        href="https://disneyworld.com/luggage"
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    />
                                                ) : (
                                                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                                                    <a href="mailto:bcsupport@bagcheck.com" />
                                                ),
                                            ]}
                                        />
                                    }
                                </p>
                            </div>
                        ))}
                    </div>
                ))}
                <PadContainer cssClassModifiers={['pad-small']} />
                <button
                    className="btn btn--primary"
                    onClick={() => navigate('..')}
                >
                    {t('backToEnrollment')}
                </button>
            </div>
        </div>
    );
}

export default BagEventsPage;
